import { addListener, getResultFromResponse, invoke, loop, runAsyncSafe } from '@web3/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getMarketTickers } from '../api';
import { EventBasedStore, pickStoreActions } from '../utils/EventBasedStore';

const defaultQuote = 'USDT';

type State = {
  priceMap: Record<string, string>;
};

class PriceStore extends EventBasedStore<State> {
  constructor() {
    super({
      priceMap: {},
    });
  }

  private _getList: Array<string> = [];
  private _flushList?: Promise<Record<string, string>>;

  /**
   * 计算获取价格
   * @param token token名字 例如BTC、ETH，默认是相对usdt
   * @param param.force 强制刷新
   * @param param.quote 计价代币，默认是USDT
   * @returns
   */
  async updatePrice(token?: string, { force = false, quote = defaultQuote } = {}) {
    if (!token) {
      return '';
    }
    const symbol = [token, quote].join('').toUpperCase();
    if (!force) {
      const price = this.state.priceMap[symbol];
      if (price) {
        return price;
      }
    }
    if (!this._getList.includes(symbol)) {
      this._getList.push(symbol);
    }

    if (!this._flushList) {
      this._flushList = invoke(async () => {
        await Promise.resolve();
        const list = this._getList;
        this._getList = [];
        this._flushList = undefined;
        const [err, data] = await runAsyncSafe(
          getResultFromResponse(
            getMarketTickers({
              symbol: list,
            }),
          ),
        );
        if (data) {
          const newPriceMap = {
            ...this.state.priceMap,
          };
          data.forEach((d) => {
            newPriceMap[d.s] = d.c;
          });
          this.setState({ priceMap: newPriceMap });
        }
        return this.state.priceMap;
      });
    }
    const res = await this._flushList;
    return res[symbol] ?? '';
  }

  getPrice(token: string, quote = defaultQuote) {
    const symbol = [token, quote].join('').toUpperCase();
    return this.state.priceMap[symbol] ?? '';
  }

  /**
   * 计算token 的价值，默认是USDT计价
   * 需要提前调用updatePrice
   * @param token 计算的token名字
   * @param quantity  数量
   * @param quote 计价代币，默认USDT
   * @returns
   */
  countAmount(token: string, quantity?: string | number, quote = defaultQuote) {
    const num = Number(quantity);
    const price = Number(this.getPrice(token, quote));
    if (!token || isNaN(price) || isNaN(num)) {
      return Number.NaN;
    }
    return price * num;
  }

  /**
   * 给予token价值，计算token的数量
   * @param token 计算的token名字
   * @param quantity  价值
   * @param quote 计价代币，默认USDT
   * @returns
   */
  countQuantity(token: string, amount?: number | string, quote = defaultQuote) {
    const num = Number(amount);
    const price = Number(this.getPrice(token, quote));
    if (!token || isNaN(price) || isNaN(num)) {
      return Number.NaN;
    }
    return num / price;
  }

  // TODO: store 级别的更新价格机制
}

export const priceStore = new PriceStore();

export const usePrice = () => {
  const state = priceStore.useStoreState();
  const actions = useMemo(() => pickStoreActions(priceStore, ['getPrice', 'countAmount', 'updatePrice', 'countQuantity']), []);
  return {
    ...state,
    ...actions,
  };
};

export const useTokenPrice = (token: string, interval?: number, quote = defaultQuote) => {
  const symbol = [token, quote].join('').toUpperCase();
  const priceRef = useRef(priceStore.getPrice(token, quote));
  const [, update] = useState(1);
  const actions = useMemo(() => {
    return {
      updatePrice: () => priceStore.updatePrice(token, { force: true, quote }),
      countAmount: (quantity?: number | string) => {
        return priceStore.countAmount(token, quantity, quote);
      },
      countQuantity: () => (amount?: number | string) => {
        return priceStore.countQuantity(token, amount, quote);
      },
    };
  }, [token, quote]);

  useEffect(() => {
    const removeLis = addListener(priceStore, 'update', ({ priceMap }) => {
      const price = priceMap[symbol] ?? '';
      if (price !== priceRef.current) {
        priceRef.current = price;
        update((n) => n + 1);
      }
    });
    const shouldLoop = interval && interval > 500;
    const stopLoop = shouldLoop
      ? loop(actions.updatePrice, {
          interval,
          immediate: true,
        })
      : undefined;

    if (!shouldLoop && !priceRef.current) {
      actions.updatePrice();
    }

    return () => {
      stopLoop?.();
      removeLis();
    };
  }, [symbol, interval, actions]);

  return {
    symbol,
    price: priceRef.current,
    ...actions,
  };
};
